import {omneticApi} from '@dms/api/base';

import {assignSafely} from 'shared/web';

import {CreateUserVehicleSummaryRequestBody} from '../sourcing/types';
import {getInfiniteScrollBoundaryOffset} from '../sourcing/utils/getInfiniteScrollBoundaryOffset';
import {List, OmneticMutationLifecycleApi} from '../sourcing/utils/sourcingApiHelpers';
import {updateListEndReached} from '../sourcing/utils/updateListEndReached';
import {updateScrollAttemptedOffset} from '../sourcing/utils/updateScrollAttemptedOffset';
import {
  AuctionServerResponseBody,
  AuctionVehicleDetailResponseBody,
  CreateAuctionUserSeenApiArg,
  CreateAuctionUserSeenApiResponse,
  ExternalIdWithPositionRequestBody,
  GetAuctionUserComparisonApiArg,
  GetAuctionUserComparisonApiResponse,
  GetAuctionUserFavouriteApiArg,
  GetAuctionUserFavouriteApiResponse,
  GetAuctionUserHiddenApiArg,
  GetAuctionUserHiddenApiResponse,
  GetAuctionUserToBuyApiArg,
  GetAuctionUserToBuyApiResponse,
  GetAuctionVehicleCountApiArg,
  GetAuctionVehicleCountApiResponse,
  GetAuctionVehicleDetailApiArg,
  GetAuctionVehicleDetailApiResponse,
  GetAuctionVehicleListApiArg,
  GetAuctionVehicleListApiResponse,
  SortAuctionUserComparisonRequestApiArg,
} from './types';
import {filterOutAuctionDuplicates} from './utils/filterOutAuctionDuplicates';
import {isAuctionListResponse} from './utils/isAuctionListResponse';
import {normalizeAuctionCurrencies} from './utils/normalizeAuctionCurrencies';

type AuctionBulkUserActionApiArg = {
  body: {externalIds: string[]};
};

const ROWS_PER_PAGE = 20;
const AUCTION_LIST_QUERY_NAMES = [
  'getSourcingAuctionList',
  'getAuctionUserFavourite',
  'getAuctionUserComparison',
  'getAuctionUserHidden',
  'getAuctionUserToBuy',
] as const;

type AuctionListEndpointName = (typeof AUCTION_LIST_QUERY_NAMES)[number];

const getOffset = (page: number = 1) => (page - 1) * ROWS_PER_PAGE;

function isAuctionListEndpointName(endpointName: string): endpointName is AuctionListEndpointName {
  return AUCTION_LIST_QUERY_NAMES.includes(endpointName as AuctionListEndpointName);
}

const sourcingAuctionApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Accumulating list query for infinite scroll. All pages for a given (filters, currency,
     * orderBy) tuple merge into a single cache entry via `merge` (append). Polling runs against
     * the separate `getSourcingAuctionListPoll` cache and prepends new items via
     * `updateQueryData`.
     */
    getSourcingAuctionList: build.query<
      GetAuctionVehicleListApiResponse,
      GetAuctionVehicleListApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle',
        method: 'POST',
        body: queryArg.body ?? {},
        params: {
          offset: getOffset(queryArg.page),
          size: ROWS_PER_PAGE,
          currency: queryArg.currency,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (rawData: GetAuctionVehicleListApiResponse, _meta, queryArg) => {
        const response = {
          ...rawData,
          data: rawData.data.map(normalizeAuctionCurrencies),
        };
        updateScrollAttemptedOffset(response, queryArg.page);
        updateListEndReached(response, response.data.length);
        return response;
      },
      serializeQueryArgs: ({endpointName, queryArgs}) =>
        // Page is intentionally NOT in the cache key — all pages for the same filter set
        // accumulate into one cache entry via `merge` (append).
        `${endpointName}_${queryArgs.currency}_${queryArgs.orderBy}_${JSON.stringify(queryArgs.body)}`,
      merge: (currentCache, newData, meta) => {
        const newVehicles = filterOutAuctionDuplicates(currentCache.data, newData.data);
        currentCache.data.push(...newVehicles);
        if (currentCache.links) {
          currentCache.links.size = currentCache.data.length;
        }
        updateScrollAttemptedOffset(currentCache, meta.arg.page);
        updateListEndReached(currentCache, newData.data.length);
      },
      forceRefetch: (params) => {
        // Mirror of sourcing/api.ts forceRefetch. See README §Infinite scroll.
        const currentPage = params.currentArg?.page ?? 1;
        const previousPage = params.previousArg?.page;
        const requestedOffset = getOffset(currentPage);
        const endpointData = params.endpointState?.data;
        const cachedLength = isAuctionListResponse(endpointData) ? endpointData.data.length : 0;
        const scrollAttemptedOffset = isAuctionListResponse(endpointData)
          ? endpointData.scrollAttemptedOffset
          : undefined;
        const cachedPageOffset = getInfiniteScrollBoundaryOffset(
          cachedLength,
          scrollAttemptedOffset
        );
        const shouldRefetch =
          requestedOffset === cachedPageOffset ||
          (requestedOffset > cachedPageOffset && currentPage === (previousPage ?? 1) + 1);

        return shouldRefetch;
      },
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
      keepUnusedDataFor: 1800,
    }),
    /**
     * Narrow polling endpoint that fetches one page of latest items without merging. The
     * AllOffers hook walks pages 1..N until it finds overlap with the accumulating cache, then
     * prepends the new items via `updateQueryData('getSourcingAuctionList', ...)`. Each page
     * lives in its own cache entry keyed by `page` and expires immediately when no subscriber
     * is listening (`keepUnusedDataFor: 0`).
     */
    getSourcingAuctionListPoll: build.query<
      GetAuctionVehicleListApiResponse,
      Omit<GetAuctionVehicleListApiArg, 'size' | 'offset'>
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle',
        method: 'POST',
        body: queryArg.body ?? {},
        params: {
          offset: getOffset(queryArg.page),
          size: ROWS_PER_PAGE,
          currency: queryArg.currency,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (rawData: GetAuctionVehicleListApiResponse) => ({
        ...rawData,
        data: rawData.data.map(normalizeAuctionCurrencies),
      }),
      keepUnusedDataFor: 0,
    }),

    getSourcingAuctionDetail: build.query<
      GetAuctionVehicleDetailApiResponse,
      GetAuctionVehicleDetailApiArg
    >({
      query: (arg) => ({
        url: `/dms/v1/auction-vehicle/${arg.externalId}`,
        method: 'GET',
        params: {
          margin: arg.margin,
          currency: arg.currency,
          buyerCountry: arg.buyerCountry,
        },
      }),
      transformResponse: (response: GetAuctionVehicleDetailApiResponse) =>
        normalizeAuctionCurrencies(response),
      providesTags: (_result, _error, arg) => [{type: 'SourcingAuctionDetail', id: arg.externalId}],
    }),

    getSourcingAuctionCount: build.query<
      GetAuctionVehicleCountApiResponse,
      GetAuctionVehicleCountApiArg
    >({
      query: (arg) => ({
        url: '/dms/v1/auction-vehicle/_count',
        method: 'POST',
        body: arg.body ?? {},
        params: {
          currency: arg.currency,
        },
      }),
      providesTags: ['SourcingAuctionList'],
    }),

    getAuctionUserFavourite: build.query<
      GetAuctionUserFavouriteApiResponse,
      GetAuctionUserFavouriteApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-favourite',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserFavouriteApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: [
        'SourcingAuctionAllListsWithSameResponseStructure',
        'SourcingAuctionFavouriteList',
      ],
    }),

    getAuctionUserComparison: build.query<
      GetAuctionUserComparisonApiResponse,
      GetAuctionUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-comparison',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserComparisonApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: [
        'SourcingAuctionAllListsWithSameResponseStructure',
        'SourcingAuctionComparisonList',
      ],
    }),

    getAuctionUserHidden: build.query<GetAuctionUserHiddenApiResponse, GetAuctionUserHiddenApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-hidden',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserHiddenApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: [
        'SourcingAuctionAllListsWithSameResponseStructure',
        'SourcingAuctionHiddenList',
      ],
    }),

    getAuctionUserToBuy: build.query<GetAuctionUserToBuyApiResponse, GetAuctionUserToBuyApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-to-buy',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserToBuyApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: [
        'SourcingAuctionAllListsWithSameResponseStructure',
        'SourcingAuctionToBuyList',
      ],
    }),

    auctionBulkDeleteUserFavourite: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-favourite/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserFavourite');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionFavouriteList',
      ],
    }),

    auctionBulkCreateUserFavourite: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-favourite/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionFavouriteList',
      ],
    }),

    auctionBulkDeleteUserComparison: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-comparison/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserComparison');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionComparisonList',
      ],
    }),

    auctionBulkCreateUserComparison: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-comparison/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionComparisonList',
      ],
    }),

    auctionReorderUserComparison: build.mutation<void, SortAuctionUserComparisonRequestApiArg>({
      query: (queryArg) => {
        const auctionVehiclesWithPositions: ExternalIdWithPositionRequestBody[] = queryArg.map(
          (vehicle, index) => ({
            externalId: vehicle.auctionVehicle.adId ?? '',
            position: index + 1,
          })
        );

        return {
          url: '/dms/v1/auction-vehicle/user-comparison/sort',
          method: 'POST',
          body: {auctionVehiclesWithPositions},
        };
      },
      onQueryStarted(patch, mutationLifecycleApi) {
        sourcingAuctionApi.util
          .selectInvalidatedBy(mutationLifecycleApi.getState(), [
            {type: 'SourcingAuctionComparisonList'},
          ])
          .forEach(({endpointName, originalArgs}) => {
            if (endpointName !== 'getAuctionUserComparison') {
              console.error(`[auctionReorderUserComparison] Unexpected endpoint: ${endpointName}`);
              return;
            }

            const patchResult = mutationLifecycleApi.dispatch(
              sourcingAuctionApi.util.updateQueryData(
                'getAuctionUserComparison',
                originalArgs,
                (draft) => {
                  draft.data.splice(0, draft.data.length, ...patch);
                }
              )
            );
            mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
          });
      },
      invalidatesTags: [{type: 'SourcingAuctionComparisonList'}],
    }),

    auctionBulkDeleteUserHidden: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-hidden/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserHidden');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionHiddenList',
      ],
    }),

    auctionBulkCreateUserHidden: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-hidden/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getSourcingAuctionList');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionHiddenList',
      ],
    }),

    auctionBulkDeleteUserToBuy: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-to-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserToBuy');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', false);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionToBuyList',
      ],
    }),

    auctionBulkCreateUserToBuy: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-to-buy/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', true);
      },
      invalidatesTags: [
        'User',
        'SourcingAuctionList',
        'SourcingAuctionDetail',
        'SourcingAuctionToBuyList',
      ],
    }),

    sourcingSetAuctionUserSeen: build.mutation<
      CreateAuctionUserSeenApiResponse,
      CreateAuctionUserSeenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-seen`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
      },
    }),

    getAuctionServerList: build.query<AuctionServerResponseBody[], void>({
      query: () => ({url: '/dms/v1/sourcing/auction-server'}),
    }),
  }),
});

function optimisticUpdateVehicleDetailCache<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (queryArgs.body.externalIds.includes(originalArgs.externalId)) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            assignSafely(draft, list, isInList);
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

function optimisticUpdateVehicleListCaches<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isAuctionListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (queryArgs.body.externalIds.includes(vehicle.auctionVehicle.adId ?? '')) {
              assignSafely(vehicle, list, isInList);
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

function optimisticRemoveVehiclesFromList<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  listToRemoveEndpointName?: AuctionListEndpointName
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isAuctionListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (listToRemoveEndpointName === undefined || endpointName === listToRemoveEndpointName) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data = draft.data.filter(
              (vehicle) => !queryArgs.body.externalIds.includes(vehicle.auctionVehicle.adId ?? '')
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

export function updateAuctionVehicleSummaryInCaches(
  sourcingVehicleId: string,
  body: CreateUserVehicleSummaryRequestBody,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isAuctionListEndpointName(endpointName)) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (vehicle.auctionVehicle.adId === sourcingVehicleId) {
              vehicle.vehicleSummary = {
                id: vehicle.vehicleSummary?.id ?? '',
                purchasePrice: body.purchasePrice ?? null,
                sellingPrice: body.sellingPrice ?? null,
                comment: body.comment ?? null,
              };
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });

  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }

      if (originalArgs.externalId !== sourcingVehicleId) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.vehicleSummary = {
            id: draft.vehicleSummary?.id ?? '',
            purchasePrice: body.purchasePrice ?? null,
            sellingPrice: body.sellingPrice ?? null,
            comment: body.comment ?? null,
          };
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

export function updateAuctionDetailCacheByExternalId(
  externalId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>,
  updateFn: (draft: AuctionVehicleDetailResponseBody) => void
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }

      if (originalArgs.externalId !== externalId) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, updateFn)
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

export {sourcingAuctionApi};

export const {
  useGetSourcingAuctionListQuery,
  useGetSourcingAuctionDetailQuery,
  useGetSourcingAuctionCountQuery,
  useGetAuctionUserFavouriteQuery,
  useGetAuctionUserComparisonQuery,
  useGetAuctionUserHiddenQuery,
  useGetAuctionUserToBuyQuery,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkDeleteUserComparisonMutation,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionReorderUserComparisonMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useSourcingSetAuctionUserSeenMutation,
  useGetAuctionServerListQuery,
} = sourcingAuctionApi;
